import type { Trade, TradeTag, TradingSession } from "wasp/entities";
import { calculatePips } from "./pipConfig";

// Net P&L for a single trade: gross pnl + commission (negative) + swaps
export function netPnl(t: Trade): number {
  return (t.pnl ?? 0) + (t.commission ?? 0) + (t.swaps ?? 0);
}

export interface TradingMetrics {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  totalPnl: number;
  winRate: number;
  profitFactor: number;
  averageWin: number;
  averageLoss: number;
  largestWin: number;
  largestLoss: number;
  maxDrawdown: number;
  sharpeRatio: number | null;
  expectancy: number;
  [key: string]: number | null; // Index signature for Wasp SuperJSON (query return) compatibility
}

export interface SymbolStats {
  symbol: string;
  trades: number;
  wins: number;
  losses: number;
  totalPnl: number;
  winRate: number;
  averagePnl: number;
}

export interface TimePeriodStats {
  period: string;
  trades: number;
  totalPnl: number;
  winRate: number;
}

export function calculateMetrics(trades: Trade[]): TradingMetrics {
  // Filter out trades without PnL (open trades)
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);

  if (closedTrades.length === 0) {
    return {
      totalTrades: trades.length,
      winningTrades: 0,
      losingTrades: 0,
      totalPnl: 0,
      winRate: 0,
      profitFactor: 0,
      averageWin: 0,
      averageLoss: 0,
      largestWin: 0,
      largestLoss: 0,
      maxDrawdown: 0,
      sharpeRatio: null,
      expectancy: 0,
    };
  }

  const winningTrades = closedTrades.filter((t) => netPnl(t) > 0);
  const losingTrades = closedTrades.filter((t) => netPnl(t) < 0);
  const totalPnl = closedTrades.reduce((sum, t) => sum + netPnl(t), 0);
  const winRate = (winningTrades.length / closedTrades.length) * 100;

  const totalWins = winningTrades.reduce((sum, t) => sum + netPnl(t), 0);
  const totalLosses = Math.abs(
    losingTrades.reduce((sum, t) => sum + netPnl(t), 0),
  );
  const profitFactor = totalLosses > 0 ? totalWins / totalLosses : totalWins;

  const averageWin =
    winningTrades.length > 0
      ? winningTrades.reduce((sum, t) => sum + netPnl(t), 0) / winningTrades.length
      : 0;
  const averageLoss =
    losingTrades.length > 0
      ? Math.abs(losingTrades.reduce((sum, t) => sum + netPnl(t), 0)) /
        losingTrades.length
      : 0;

  const largestWin =
    winningTrades.length > 0
      ? winningTrades.reduce((m, t) => Math.max(m, netPnl(t)), -Infinity)
      : 0;
  const largestLoss =
    losingTrades.length > 0
      ? losingTrades.reduce((m, t) => Math.min(m, netPnl(t)), Infinity)
      : 0;

  const maxDrawdown = calculateMaxDrawdown(closedTrades);
  const sharpeRatio = calculateSharpeRatio(closedTrades);
  const expectancy = calculateExpectancy(closedTrades);

  return {
    totalTrades: closedTrades.length,
    winningTrades: winningTrades.length,
    losingTrades: losingTrades.length,
    totalPnl,
    winRate,
    profitFactor,
    averageWin,
    averageLoss,
    largestWin,
    largestLoss,
    maxDrawdown,
    sharpeRatio,
    expectancy,
  };
}

function calculateSharpeRatio(trades: Trade[]): number | null {
  // Filter out trades without PnL
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closedTrades.length < 2) return null;

  // Sort trades by date
  const sortedTrades = [...closedTrades].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const returns = sortedTrades.map((t) => netPnl(t));
  const meanReturn =
    returns.reduce((sum, r) => sum + r, 0) / returns.length;

  const variance =
    returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) /
    (returns.length - 1);
  const stdDev = Math.sqrt(variance);

  if (stdDev === 0) return null;

  // Assuming risk-free rate of 0 for simplicity
  // In production, you might want to use actual risk-free rate
  const sharpeRatio = meanReturn / stdDev;

  return sharpeRatio;
}

function calculateExpectancy(trades: Trade[]): number {
  // Filter out trades without PnL
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closedTrades.length === 0) return 0;

  const winningTrades = closedTrades.filter((t) => netPnl(t) > 0);
  const losingTrades = closedTrades.filter((t) => netPnl(t) < 0);

  const winRate = winningTrades.length / closedTrades.length;
  const averageWin =
    winningTrades.length > 0
      ? winningTrades.reduce((sum, t) => sum + netPnl(t), 0) / winningTrades.length
      : 0;
  const averageLoss =
    losingTrades.length > 0
      ? Math.abs(losingTrades.reduce((sum, t) => sum + netPnl(t), 0)) /
        losingTrades.length
      : 0;

  return winRate * averageWin - (1 - winRate) * averageLoss;
}

export function calculateSymbolStats(trades: Trade[]): SymbolStats[] {
  // Filter out trades without PnL
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  
  const symbolMap = new Map<string, Trade[]>();

  for (const trade of closedTrades) {
    const symbol = trade.symbol;
    if (!symbolMap.has(symbol)) {
      symbolMap.set(symbol, []);
    }
    symbolMap.get(symbol)!.push(trade);
  }

  const stats: SymbolStats[] = [];

  for (const [symbol, symbolTrades] of symbolMap.entries()) {
    const wins = symbolTrades.filter((t) => netPnl(t) > 0).length;
    const losses = symbolTrades.filter((t) => netPnl(t) < 0).length;
    const totalPnl = symbolTrades.reduce((sum, t) => sum + netPnl(t), 0);
    const winRate = symbolTrades.length > 0 ? (wins / symbolTrades.length) * 100 : 0;
    const averagePnl = symbolTrades.length > 0 ? totalPnl / symbolTrades.length : 0;

    stats.push({
      symbol,
      trades: symbolTrades.length,
      wins,
      losses,
      totalPnl,
      winRate,
      averagePnl,
    });
  }

  return stats.sort((a, b) => b.totalPnl - a.totalPnl);
}

export function calculateTimePeriodStats(
  trades: Trade[],
  period: "day" | "week" | "month",
): TimePeriodStats[] {
  // Filter out trades without PnL
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  
  const periodMap = new Map<string, Trade[]>();

  for (const trade of closedTrades) {
    const date = new Date(trade.date);
    let key: string;

    switch (period) {
      case "day":
        key = date.toISOString().split("T")[0];
        break;
      case "week":
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split("T")[0];
        break;
      case "month":
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        break;
    }

    if (!periodMap.has(key)) {
      periodMap.set(key, []);
    }
    periodMap.get(key)!.push(trade);
  }

  const stats: TimePeriodStats[] = [];

  for (const [periodKey, periodTrades] of periodMap.entries()) {
    const wins = periodTrades.filter((t) => netPnl(t) > 0).length;
    const totalPnl = periodTrades.reduce((sum, t) => sum + netPnl(t), 0);
    const winRate = periodTrades.length > 0 ? (wins / periodTrades.length) * 100 : 0;

    stats.push({
      period: periodKey,
      trades: periodTrades.length,
      totalPnl,
      winRate,
    });
  }

  return stats.sort((a, b) => a.period.localeCompare(b.period));
}

export function calculateCumulativePnl(trades: Trade[]): Array<{
  date: string;
  cumulativePnl: number;
}> {
  // Filter out trades without PnL
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  
  // Sort trades by date
  const sortedTrades = [...closedTrades].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  let cumulativePnl = 0;
  const data: Array<{ date: string; cumulativePnl: number }> = [];

  for (const trade of sortedTrades) {
    cumulativePnl += netPnl(trade);
    data.push({
      date: new Date(trade.date).toISOString().split("T")[0],
      cumulativePnl,
    });
  }

  return data;
}

// ─── New advanced analytics functions ───────────────────────────────────────

export interface HeatmapCell {
  day: number;   // 0=Sun … 6=Sat
  hour: number;  // 0-23
  pnl: number;
  count: number;
  winRate: number;
}

export function calculateHourDayHeatmap(trades: Trade[]): HeatmapCell[] {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  const map = new Map<string, { pnl: number; count: number; wins: number }>();

  for (const t of closed) {
    const d = new Date(t.date);
    const key = `${d.getDay()}-${d.getHours()}`;
    const existing = map.get(key) || { pnl: 0, count: 0, wins: 0 };
    existing.pnl += netPnl(t);
    existing.count += 1;
    if (netPnl(t) > 0) existing.wins += 1;
    map.set(key, existing);
  }

  const result: HeatmapCell[] = [];
  for (const [key, val] of map.entries()) {
    const [day, hour] = key.split("-").map(Number);
    result.push({ day, hour, pnl: val.pnl, count: val.count, winRate: val.count > 0 ? (val.wins / val.count) * 100 : 0 });
  }
  return result;
}

export interface TimeSlotStats {
  key: "preMarket" | "open" | "midDay" | "afternoon" | "powerHour";
  startHour: number;
  endHour: number;
  pnl: number;
  count: number;
  winRate: number;
  avgPnl: number;
}

export function calculateTimeSlotStats(trades: Trade[]): TimeSlotStats[] {
  const slots: { key: TimeSlotStats["key"]; startHour: number; endHour: number }[] = [
    { key: "preMarket",  startHour: 0,  endHour: 9  },
    { key: "open",       startHour: 9,  endHour: 11 },
    { key: "midDay",     startHour: 11, endHour: 14 },
    { key: "afternoon",  startHour: 14, endHour: 16 },
    { key: "powerHour",  startHour: 16, endHour: 24 },
  ];

  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);

  return slots.map((slot) => {
    const slotTrades = closed.filter((t) => {
      const h = new Date(t.date).getHours();
      return h >= slot.startHour && h < slot.endHour;
    });
    const pnl = slotTrades.reduce((s, t) => s + netPnl(t), 0);
    const wins = slotTrades.filter((t) => netPnl(t) > 0).length;
    return {
      ...slot,
      pnl,
      count: slotTrades.length,
      winRate: slotTrades.length > 0 ? (wins / slotTrades.length) * 100 : 0,
      avgPnl: slotTrades.length > 0 ? pnl / slotTrades.length : 0,
    };
  });
}

export interface HoldDurationPoint {
  durationMinutes: number;
  pnl: number;
  symbol: string;
  isWin: boolean;
}

export function calculateHoldDurationData(trades: (Trade & { exitDate?: Date | null })[]): HoldDurationPoint[] {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  return closed
    .map((t) => {
      const entry = new Date(t.date).getTime();
      const exit = t.exitDate ? new Date(t.exitDate).getTime() : null;
      if (!exit || exit <= entry) return null;
      const durationMinutes = Math.round((exit - entry) / 60000);
      return {
        durationMinutes,
        pnl: netPnl(t),
        symbol: t.symbol,
        isWin: netPnl(t) > 0,
      };
    })
    .filter((x): x is HoldDurationPoint => x !== null && x.durationMinutes > 0 && x.durationMinutes < 10000);
}

export interface TagStats {
  tag: string;
  trades: number;
  wins: number;
  losses: number;
  totalPnl: number;
  winRate: number;
  avgPnl: number;
}

export function calculateTagStats(trades: (Trade & { tags?: TradeTag[] })[]): TagStats[] {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  const map = new Map<string, { pnl: number; wins: number; losses: number; count: number }>();

  for (const t of closed) {
    const tags: string[] = (t as any).tags?.map((tag: any) => typeof tag === "string" ? tag : tag.name) || [];
    for (const tag of tags) {
      const existing = map.get(tag) || { pnl: 0, wins: 0, losses: 0, count: 0 };
      existing.pnl += netPnl(t);
      existing.count += 1;
      if (netPnl(t) > 0) existing.wins += 1;
      else if (netPnl(t) < 0) existing.losses += 1;
      map.set(tag, existing);
    }
  }

  const result: TagStats[] = [];
  for (const [tag, val] of map.entries()) {
    result.push({
      tag,
      trades: val.count,
      wins: val.wins,
      losses: val.losses,
      totalPnl: val.pnl,
      winRate: val.count > 0 ? (val.wins / val.count) * 100 : 0,
      avgPnl: val.count > 0 ? val.pnl / val.count : 0,
    });
  }
  return result.sort((a, b) => b.totalPnl - a.totalPnl);
}

export interface MonthlyStats {
  month: string; // "YYYY-MM"
  trades: number;
  wins: number;
  losses: number;
  totalPnl: number;
  winRate: number;
  profitFactor: number;
}

export type ReportGranularity = "month" | "quarter" | "year";

export interface PeriodReport {
  label: string;       // "Jan 2024", "Q1 2024", "2024"
  key: string;         // sortable: "2024-01", "2024-Q1", "2024"
  trades: number;
  wins: number;
  losses: number;
  grossPnl: number;    // sum of trade.pnl (before commissions/swaps)
  commissions: number; // sum of trade.commission (always negative cost)
  swaps: number;       // sum of trade.swaps
  netPnl: number;      // grossPnl + commissions + swaps
  winRate: number;
  profitFactor: number;
  avgNetPnl: number;
  bestTrade: number;
  worstTrade: number;
}

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export function calculatePeriodReport(trades: Trade[], granularity: ReportGranularity): PeriodReport[] {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  const map = new Map<string, Trade[]>();

  for (const t of closed) {
    const d = new Date(t.date);
    const y = d.getFullYear();
    const m = d.getMonth();
    let key: string;
    if (granularity === "month") {
      key = `${y}-${String(m + 1).padStart(2, "0")}`;
    } else if (granularity === "quarter") {
      key = `${y}-Q${Math.floor(m / 3) + 1}`;
    } else {
      key = `${y}`;
    }
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(t);
  }

  const result: PeriodReport[] = [];
  for (const [key, pts] of map.entries()) {
    const wins = pts.filter((t) => netPnl(t) > 0);
    const losses = pts.filter((t) => netPnl(t) < 0);
    const grossPnlSum = pts.reduce((s, t) => s + (t.pnl ?? 0), 0);
    const commissionsSum = pts.reduce((s, t) => s + (t.commission ?? 0), 0);
    const swapsSum = pts.reduce((s, t) => s + (t.swaps ?? 0), 0);
    const netPnlSum = pts.reduce((s, t) => s + netPnl(t), 0);
    const totalWins = wins.reduce((s, t) => s + netPnl(t), 0);
    const totalLosses = Math.abs(losses.reduce((s, t) => s + netPnl(t), 0));
    const tradeNetPnls = pts.map((t) => netPnl(t));

    // Build human label
    let label: string;
    if (granularity === "month") {
      const [y, m] = key.split("-");
      label = `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
    } else if (granularity === "quarter") {
      label = key.replace("-", " ");
    } else {
      label = key;
    }

    result.push({
      label,
      key,
      trades: pts.length,
      wins: wins.length,
      losses: losses.length,
      grossPnl: grossPnlSum,
      commissions: commissionsSum,
      swaps: swapsSum,
      netPnl: netPnlSum,
      winRate: pts.length > 0 ? (wins.length / pts.length) * 100 : 0,
      profitFactor: totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? Infinity : 0,
      avgNetPnl: pts.length > 0 ? netPnlSum / pts.length : 0,
      bestTrade: tradeNetPnls.length > 0 ? Math.max(...tradeNetPnls) : 0,
      worstTrade: tradeNetPnls.length > 0 ? Math.min(...tradeNetPnls) : 0,
    });
  }
  return result.sort((a, b) => a.key.localeCompare(b.key));
}

export function calculateMonthlyStats(trades: Trade[]): MonthlyStats[] {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  const map = new Map<string, Trade[]>();

  for (const t of closed) {
    const key = new Date(t.date).toISOString().substring(0, 7);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(t);
  }

  const result: MonthlyStats[] = [];
  for (const [month, monthTrades] of map.entries()) {
    const wins = monthTrades.filter((t) => netPnl(t) > 0);
    const losses = monthTrades.filter((t) => netPnl(t) < 0);
    const totalPnl = monthTrades.reduce((s, t) => s + netPnl(t), 0);
    const totalWins = wins.reduce((s, t) => s + netPnl(t), 0);
    const totalLosses = Math.abs(losses.reduce((s, t) => s + netPnl(t), 0));
    result.push({
      month,
      trades: monthTrades.length,
      wins: wins.length,
      losses: losses.length,
      totalPnl,
      winRate: monthTrades.length > 0 ? (wins.length / monthTrades.length) * 100 : 0,
      profitFactor: totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? Infinity : 0,
    });
  }
  return result.sort((a, b) => a.month.localeCompare(b.month));
}

export interface RollingWinRate {
  tradeIndex: number;
  winRate: number;
  date: string;
}

export function calculateRollingWinRate(trades: Trade[], window: number = 20): RollingWinRate[] {
  const closed = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const result: RollingWinRate[] = [];
  for (let i = window - 1; i < closed.length; i++) {
    const slice = closed.slice(i - window + 1, i + 1);
    const wins = slice.filter((t) => netPnl(t) > 0).length;
    result.push({
      tradeIndex: i + 1,
      winRate: (wins / window) * 100,
      date: new Date(closed[i].date).toISOString().split("T")[0],
    });
  }
  return result;
}

export interface SizingPoint {
  tradeIndex: number;
  absSize: number;
  pnl: number;
  date: string;
  isWin: boolean;
}

export function calculateSizingData(trades: Trade[]): SizingPoint[] {
  const closed = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return closed.map((t, i) => ({
    tradeIndex: i + 1,
    absSize: Math.abs(netPnl(t)),
    pnl: netPnl(t),
    date: new Date(t.date).toISOString().split("T")[0],
    isWin: netPnl(t) > 0,
  }));
}

export interface BehavioralBiases {
  fomoIndicator: number;        // % trades opened right after a loss within 10min
  lossAversionRatio: number;    // avg duration of losers / avg duration of winners (needs exitDate)
  revengeTradingCount: number;  // trades within 30min of a loss
  overconfidenceScore: number;  // after 3+ win streak, next trade bigger? ratio
}

export function calculateBehavioralBiases(
  trades: (Trade & { exitDate?: Date | null })[],
): BehavioralBiases {
  const closed = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  let revengeCount = 0;
  let fomoCount = 0;
  let fomoOpportunities = 0;

  for (let i = 1; i < closed.length; i++) {
    const prev = closed[i - 1];
    const curr = closed[i];
    const diffMin = (new Date(curr.date).getTime() - new Date(prev.date).getTime()) / 60000;
    if (netPnl(prev) < 0) {
      fomoOpportunities++;
      if (diffMin < 30) revengeCount++;
      if (diffMin < 10) fomoCount++;
    }
  }

  // Loss aversion: holders of losers vs winners
  let winDurations: number[] = [];
  let lossDurations: number[] = [];
  for (const t of closed) {
    if (!t.exitDate) continue;
    const dur = (new Date(t.exitDate).getTime() - new Date(t.date).getTime()) / 60000;
    if (dur > 0 && dur < 10000) {
      if (netPnl(t) > 0) winDurations.push(dur);
      else lossDurations.push(dur);
    }
  }
  const avgWinDur = winDurations.length > 0 ? winDurations.reduce((s, v) => s + v, 0) / winDurations.length : 0;
  const avgLossDur = lossDurations.length > 0 ? lossDurations.reduce((s, v) => s + v, 0) / lossDurations.length : 0;
  const lossAversionRatio = avgWinDur > 0 ? avgLossDur / avgWinDur : 0;

  // Overconfidence: after 3+ win streak, size up?
  let overconfidenceOk = 0;
  let overconfidenceTotal = 0;
  let streak = 1;
  for (let i = 1; i < closed.length; i++) {
    if (netPnl(closed[i - 1]) > 0) {
      streak++;
      if (streak >= 3 && i + 1 < closed.length) {
        overconfidenceTotal++;
        const avgStreak = Math.abs(netPnl(closed[i]));
        const next = Math.abs(netPnl(closed[i + 1]));
        if (next >= avgStreak) overconfidenceOk++;
      }
    } else {
      streak = 1;
    }
  }

  return {
    fomoIndicator: fomoOpportunities > 0 ? (fomoCount / fomoOpportunities) * 100 : 0,
    lossAversionRatio,
    revengeTradingCount: revengeCount,
    overconfidenceScore: overconfidenceTotal > 0 ? (overconfidenceOk / overconfidenceTotal) * 100 : 0,
  };
}

export function calculateCalmarRatio(trades: Trade[]): number | null {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closed.length < 2) return null;
  const sorted = [...closed].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const firstDate = new Date(sorted[0].date);
  const lastDate = new Date(sorted[sorted.length - 1].date);
  const years = (lastDate.getTime() - firstDate.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years < 0.01) return null;
  const totalPnl = sorted.reduce((s, t) => s + netPnl(t), 0);
  const annualized = totalPnl / years;
  const maxDD = calculateMaxDrawdown(closed);
  if (maxDD === 0) return null;
  return annualized / maxDD;
}

export function calculateRecoveryFactor(trades: Trade[]): number | null {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closed.length === 0) return null;
  const totalPnl = closed.reduce((s, t) => s + netPnl(t), 0);
  const maxDD = calculateMaxDrawdown(closed);
  if (maxDD === 0) return totalPnl > 0 ? Infinity : null;
  return totalPnl / maxDD;
}

export function calculateMaxDrawdown(trades: Trade[]): number {
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closedTrades.length === 0) return 0;

  const sortedTrades = [...closedTrades].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  let cumulativePnl = 0;
  let peak = 0;
  let maxDrawdown = 0;

  for (const trade of sortedTrades) {
    cumulativePnl += netPnl(trade);
    if (cumulativePnl > peak) {
      peak = cumulativePnl;
    }
    const drawdown = peak - cumulativePnl;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
    }
  }

  return maxDrawdown;
}

// ─────────────────────────────────────────────────────────────────────────────

export function calculatePnlDistribution(trades: Trade[]): Array<{
  range: string;
  count: number;
}> {
  // Filter out trades without PnL
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closedTrades.length === 0) return [];

  const pnls = closedTrades.map((t) => netPnl(t));
  let min = Infinity, max = -Infinity;
  for (const v of pnls) { if (v < min) min = v; if (v > max) max = v; }
  
  // Determine appropriate range size based on data spread
  const absMax = Math.max(Math.abs(min), Math.abs(max));
  let stepSize: number;
  
  if (absMax <= 50) {
    stepSize = 10; // Small ranges for small P&L
  } else if (absMax <= 200) {
    stepSize = 25; // Medium ranges
  } else if (absMax <= 500) {
    stepSize = 50; // Larger ranges
  } else if (absMax <= 1000) {
    stepSize = 100; // Even larger ranges
  } else {
    stepSize = 250; // Very large ranges
  }

  // Create clear, rounded ranges
  const ranges: Array<{ min: number; max: number; label: string }> = [];
  
  // Negative ranges
  let current = Math.floor(min / stepSize) * stepSize;
  while (current < 0) {
    ranges.push({
      min: current,
      max: current + stepSize,
      label: `${current} $ à ${current + stepSize} $`,
    });
    current += stepSize;
  }
  
  // Positive ranges
  current = 0;
  while (current < max + stepSize) {
    const rangeMax = current + stepSize;
    ranges.push({
      min: current,
      max: rangeMax,
      label: current === 0 
        ? `0 $ à ${stepSize} $`
        : `${current} $ à ${rangeMax} $`,
    });
    current += stepSize;
  }

  const buckets = new Map<string, number>();

  // Count trades in each range
  for (const pnl of pnls) {
    for (const range of ranges) {
      // Handle the last range (max value)
      if (range.max === Infinity) {
        if (pnl >= range.min) {
          buckets.set(range.label, (buckets.get(range.label) || 0) + 1);
          break;
        }
      } else if (pnl >= range.min && pnl < range.max) {
        buckets.set(range.label, (buckets.get(range.label) || 0) + 1);
        break;
      }
    }
  }

  // Return only ranges with trades, sorted
  return Array.from(buckets.entries())
    .map(([range, count]) => ({ range, count }))
    .sort((a, b) => {
      // Sort by the first number in the range
      const aStart = parseFloat(a.range.match(/-?\d+/)?.[0] || "0");
      const bStart = parseFloat(b.range.match(/-?\d+/)?.[0] || "0");
      return aStart - bStart;
    });
}

export type InsightType = "timeBucket" | "mood" | "duration" | "none";

export interface TimeInSessionBucket {
  label: string;
  pnl: number;
  winRate: number;
  tradeCount: number;
}

export interface MoodStat {
  mood: string;
  avgPnl: number;
  sessionCount: number;
}

export interface DurationBucket {
  label: string;
  avgPnl: number;
  sessionCount: number;
}

export interface OutsideSessionStats {
  tradeCount: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  totalPnl: number;
  avgPnl: number;
  profitFactor: number;
  pctOfTotalTrades: number;
  pctOfTotalPnl: number;
}

export interface SessionStats {
  timeInSessionBuckets: TimeInSessionBucket[];
  moodStats: MoodStat[];
  durationBuckets: DurationBucket[];
  outsideSession: OutsideSessionStats;
  insight: {
    type: InsightType;
    bucketLabel?: string;
    pct?: number;
    mood?: string;
    avgPnl?: number;
    durationLabel?: string;
  };
  hasEnoughData: boolean;
}

const TIME_BUCKETS = [
  { label: "0–30 min",   min: 0,   max: 30  },
  { label: "30–60 min",  min: 30,  max: 60  },
  { label: "60–90 min",  min: 60,  max: 90  },
  { label: "90–120 min", min: 90,  max: 120 },
  { label: "> 120 min",  min: 120, max: Infinity },
];

const DURATION_BUCKETS = [
  { label: "< 30 min",     min: 0,   max: 30  },
  { label: "30–60 min",    min: 30,  max: 60  },
  { label: "60–120 min",   min: 60,  max: 120 },
  { label: "120–180 min",  min: 120, max: 180 },
  { label: "> 180 min",    min: 180, max: Infinity },
];

export function calculateSessionStats(
  sessions: TradingSession[],
  trades: Trade[]
): SessionStats {
  const validSessions = sessions.filter((s) => s.startedAt && s.endedAt);
  // hasEnoughData covers time-in-session charts that need trades linked to sessions.
  // mood/duration charts only need sessions with a mood/duration, so they render independently.
  const hasEnoughData = sessions.length >= 1;

  // ── Pre-compute real PnL per session by matching trades by timestamp ──
  // session.totalPnl is always 0 in DB (never updated), so we derive it from trades.
  const sessionPnlMap = new Map<string, number>();
  for (const s of validSessions) {
    const start = new Date(s.startedAt!).getTime();
    const end = new Date(s.endedAt!).getTime();
    let pnl = 0;
    for (const trade of trades) {
      if (trade.pnl === null || trade.pnl === undefined) continue;
      const tradeTime = new Date((trade as any).exitDate ?? trade.date).getTime();
      if (tradeTime >= start && tradeTime <= end) {
        pnl += (trade.pnl || 0) + (trade.commission || 0) + ((trade as any).swaps || 0);
      }
    }
    sessionPnlMap.set(s.id, Math.round(pnl * 100) / 100);
  }

  // ── Time-in-session buckets ──────────────────────────────────────────
  const bucketAccum: Record<string, { pnl: number; wins: number; total: number }> = {};
  for (const b of TIME_BUCKETS) bucketAccum[b.label] = { pnl: 0, wins: 0, total: 0 };

  for (const trade of trades) {
    if (trade.pnl === null || trade.pnl === undefined) continue;
    const entryTime = new Date(trade.date).getTime();
    const session = validSessions.find((s) => {
      const start = new Date(s.startedAt!).getTime();
      const end = new Date(s.endedAt!).getTime();
      return entryTime >= start && entryTime <= end;
    });
    if (!session) continue;
    const minutesIn = (entryTime - new Date(session.startedAt!).getTime()) / 60000;
    const bucket = TIME_BUCKETS.find((b) => minutesIn >= b.min && minutesIn < b.max);
    if (!bucket) continue;
    const acc = bucketAccum[bucket.label];
    acc.pnl += netPnl(trade);
    acc.total += 1;
    if (netPnl(trade) > 0) acc.wins += 1;
  }

  const timeInSessionBuckets: TimeInSessionBucket[] = TIME_BUCKETS.map((b) => {
    const acc = bucketAccum[b.label];
    return {
      label: b.label,
      pnl: Math.round(acc.pnl * 100) / 100,
      winRate: acc.total > 0 ? Math.round((acc.wins / acc.total) * 100) : 0,
      tradeCount: acc.total,
    };
  });

  // ── Mood stats ───────────────────────────────────────────────────────
  const moodOrder = ["excellent", "good", "neutral", "poor", "bad"];
  const moodAccum: Record<string, { sum: number; count: number }> = {};
  for (const m of moodOrder) moodAccum[m] = { sum: 0, count: 0 };
  for (const s of sessions) {
    if (!s.mood) continue;
    const key = s.mood.toLowerCase();
    if (moodAccum[key] !== undefined) {
      moodAccum[key].sum += sessionPnlMap.get(s.id) ?? 0;
      moodAccum[key].count += 1;
    }
  }
  const moodStats: MoodStat[] = moodOrder
    .filter((m) => moodAccum[m].count > 0)
    .map((m) => ({
      mood: m,
      avgPnl: Math.round((moodAccum[m].sum / moodAccum[m].count) * 100) / 100,
      sessionCount: moodAccum[m].count,
    }));

  // ── Duration buckets ─────────────────────────────────────────────────
  const durAccum: Record<string, { sum: number; count: number }> = {};
  for (const b of DURATION_BUCKETS) durAccum[b.label] = { sum: 0, count: 0 };
  for (const s of sessions) {
    const dur = s.actualDuration ?? (
      s.startedAt && s.endedAt
        ? Math.round((new Date(s.endedAt).getTime() - new Date(s.startedAt).getTime()) / 60000)
        : null
    );
    if (dur === null || dur < 0) continue;
    const bucket = DURATION_BUCKETS.find((b) => dur >= b.min && dur < b.max);
    if (!bucket) continue;
    durAccum[bucket.label].sum += sessionPnlMap.get(s.id) ?? 0;
    durAccum[bucket.label].count += 1;
  }
  const durationBuckets: DurationBucket[] = DURATION_BUCKETS
    .filter((b) => durAccum[b.label].count > 0)
    .map((b) => ({
      label: b.label,
      avgPnl: Math.round((durAccum[b.label].sum / durAccum[b.label].count) * 100) / 100,
      sessionCount: durAccum[b.label].count,
    }));

  // ── Outside-session stats ────────────────────────────────────────────
  const closedTrades = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  const outsideTrades = closedTrades.filter((trade) => {
    const tradeTime = new Date((trade as any).exitDate ?? trade.date).getTime();
    return !validSessions.some((s) => {
      const start = new Date(s.startedAt!).getTime();
      const end = new Date(s.endedAt!).getTime();
      return tradeTime >= start && tradeTime <= end;
    });
  });
  const outsideWins = outsideTrades.filter((t) => netPnl(t) > 0);
  const outsideLosses = outsideTrades.filter((t) => netPnl(t) < 0);
  const outsideTotalPnl = outsideTrades.reduce((s, t) => s + netPnl(t), 0);
  const outsideTotalWins = outsideWins.reduce((s, t) => s + netPnl(t), 0);
  const outsideTotalLossAbs = Math.abs(outsideLosses.reduce((s, t) => s + netPnl(t), 0));
  const allPnl = closedTrades.reduce((s, t) => s + netPnl(t), 0);
  const outsideSession: OutsideSessionStats = {
    tradeCount: outsideTrades.length,
    winCount: outsideWins.length,
    lossCount: outsideLosses.length,
    winRate: outsideTrades.length > 0 ? Math.round((outsideWins.length / outsideTrades.length) * 100) : 0,
    totalPnl: Math.round(outsideTotalPnl * 100) / 100,
    avgPnl: outsideTrades.length > 0 ? Math.round((outsideTotalPnl / outsideTrades.length) * 100) / 100 : 0,
    profitFactor: outsideTotalLossAbs > 0 ? Math.round((outsideTotalWins / outsideTotalLossAbs) * 100) / 100 : outsideTotalWins > 0 ? Infinity : 0,
    pctOfTotalTrades: closedTrades.length > 0 ? Math.round((outsideTrades.length / closedTrades.length) * 100) : 0,
    pctOfTotalPnl: allPnl !== 0 ? Math.round((outsideTotalPnl / Math.abs(allPnl)) * 100) : 0,
  };

  // ── Auto insight ─────────────────────────────────────────────────────
  let insight: SessionStats["insight"] = { type: "none" };

  const bestTimeBucket = [...timeInSessionBuckets]
    .filter((b) => b.tradeCount > 0)
    .sort((a, b) => b.pnl - a.pnl)[0];
  const totalTimePnl = timeInSessionBuckets.reduce((s, b) => s + Math.abs(b.pnl), 0);
  if (bestTimeBucket && totalTimePnl > 0) {
    const pct = Math.round((Math.abs(bestTimeBucket.pnl) / totalTimePnl) * 100);
    if (pct >= 40) {
      insight = { type: "timeBucket", bucketLabel: bestTimeBucket.label, pct };
    }
  }
  if (insight.type === "none" && moodStats.length > 0) {
    const bestMood = [...moodStats].sort((a, b) => b.avgPnl - a.avgPnl)[0];
    if (bestMood.avgPnl > 0) {
      insight = { type: "mood", mood: bestMood.mood, avgPnl: bestMood.avgPnl };
    }
  }
  if (insight.type === "none" && durationBuckets.length > 0) {
    const bestDur = [...durationBuckets].sort((a, b) => b.avgPnl - a.avgPnl)[0];
    if (bestDur.avgPnl > 0) {
      insight = { type: "duration", durationLabel: bestDur.label, avgPnl: bestDur.avgPnl };
    }
  }

  return { timeInSessionBuckets, moodStats, durationBuckets, outsideSession, insight, hasEnoughData };
}

export interface PipStats {
  tradeCount: number;
  avgPips: number;
  avgPipsWin: number;
  avgPipsLoss: number;
  totalPipsWon: number;
  totalPipsLost: number;
  pipWinLossRatio: number;
  bestTradePips: number;
  worstTradePips: number;
}

export function calculatePipStats(trades: Trade[]): PipStats {
  const withPips = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .map((t) => ({ trade: t, pips: calculatePips(t) }))
    .filter((x): x is { trade: Trade; pips: number } => x.pips !== null);

  if (withPips.length === 0) {
    return { tradeCount: 0, avgPips: 0, avgPipsWin: 0, avgPipsLoss: 0, totalPipsWon: 0, totalPipsLost: 0, pipWinLossRatio: 0, bestTradePips: 0, worstTradePips: 0 };
  }

  const winners = withPips.filter((x) => x.pips > 0);
  const losers = withPips.filter((x) => x.pips < 0);

  const totalPipsWon = winners.reduce((s, x) => s + x.pips, 0);
  const totalPipsLost = losers.reduce((s, x) => s + x.pips, 0);
  const avgPipsWin = winners.length > 0 ? totalPipsWon / winners.length : 0;
  const avgPipsLoss = losers.length > 0 ? totalPipsLost / losers.length : 0;
  const pipWinLossRatio = avgPipsLoss !== 0 ? Math.abs(avgPipsWin / avgPipsLoss) : avgPipsWin > 0 ? Infinity : 0;
  const allPips = withPips.map((x) => x.pips);

  return {
    tradeCount: withPips.length,
    avgPips: allPips.reduce((s, v) => s + v, 0) / allPips.length,
    avgPipsWin,
    avgPipsLoss,
    totalPipsWon,
    totalPipsLost,
    pipWinLossRatio,
    bestTradePips: Math.max(...allPips),
    worstTradePips: Math.min(...allPips),
  };
}

export interface PipBucket {
  label: string;
  minPips: number;
  maxPips: number;
  count: number;
  wins: number;
  winRate: number;
  avgPnl: number;
}

const PIP_BUCKETS_DEF = [
  { label: "< 5 pips",    minPips: 0,   maxPips: 5   },
  { label: "5–15 pips",   minPips: 5,   maxPips: 15  },
  { label: "15–30 pips",  minPips: 15,  maxPips: 30  },
  { label: "30–60 pips",  minPips: 30,  maxPips: 60  },
  { label: "60–100 pips", minPips: 60,  maxPips: 100 },
  { label: "100+ pips",   minPips: 100, maxPips: Infinity },
];

export function calculatePipBuckets(trades: Trade[]): PipBucket[] {
  const withPips = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .map((t) => ({ pips: calculatePips(t), pnl: netPnl(t) }))
    .filter((x): x is { pips: number; pnl: number } => x.pips !== null);

  return PIP_BUCKETS_DEF
    .map((def) => {
      const bucket = withPips.filter((x) => Math.abs(x.pips) >= def.minPips && Math.abs(x.pips) < def.maxPips);
      const wins = bucket.filter((x) => x.pips > 0).length;
      const totalPnl = bucket.reduce((s, x) => s + x.pnl, 0);
      return {
        label: def.label,
        minPips: def.minPips,
        maxPips: def.maxPips,
        count: bucket.length,
        wins,
        winRate: bucket.length > 0 ? (wins / bucket.length) * 100 : 0,
        avgPnl: bucket.length > 0 ? totalPnl / bucket.length : 0,
      };
    })
    .filter((b) => b.count > 0);
}

export interface PipSymbolStat {
  symbol: string;
  trades: number;
  avgPips: number;
  totalPips: number;
  wins: number;
  winRate: number;
  avgPnl: number;
}

export function calculatePipsBySymbol(trades: Trade[]): PipSymbolStat[] {
  const withPips = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .map((t) => ({ symbol: t.symbol, pips: calculatePips(t), pnl: netPnl(t) }))
    .filter((x): x is { symbol: string; pips: number; pnl: number } => x.pips !== null);

  const map = new Map<string, { pips: number[]; pnls: number[]; wins: number }>();
  for (const x of withPips) {
    const existing = map.get(x.symbol) ?? { pips: [], pnls: [], wins: 0 };
    existing.pips.push(x.pips);
    existing.pnls.push(x.pnl);
    if (x.pips > 0) existing.wins += 1;
    map.set(x.symbol, existing);
  }

  const result: PipSymbolStat[] = [];
  for (const [symbol, data] of map.entries()) {
    const totalPips = data.pips.reduce((s, v) => s + v, 0);
    const totalPnl = data.pnls.reduce((s, v) => s + v, 0);
    result.push({
      symbol,
      trades: data.pips.length,
      avgPips: totalPips / data.pips.length,
      totalPips,
      wins: data.wins,
      winRate: (data.wins / data.pips.length) * 100,
      avgPnl: totalPnl / data.pnls.length,
    });
  }
  return result.sort((a, b) => Math.abs(b.totalPips) - Math.abs(a.totalPips));
}

// ── Long vs Short ──────────────────────────────────────────────────────────
export interface LongShortStats {
  longPnl: number;
  shortPnl: number;
  longCount: number;
  shortCount: number;
  longWinRate: number;
  shortWinRate: number;
  longAvgPnl: number;
  shortAvgPnl: number;
}

export function calculateLongShortStats(trades: Trade[]): LongShortStats {
  const longTrades = trades.filter((t) => t.type === "long" && t.pnl !== null);
  const shortTrades = trades.filter((t) => t.type === "short" && t.pnl !== null);

  const longPnl = longTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const shortPnl = shortTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);

  const longWins = longTrades.filter((t) => (t.pnl || 0) > 0).length;
  const shortWins = shortTrades.filter((t) => (t.pnl || 0) > 0).length;

  return {
    longPnl,
    shortPnl,
    longCount: longTrades.length,
    shortCount: shortTrades.length,
    longWinRate: longTrades.length > 0 ? (longWins / longTrades.length) * 100 : 0,
    shortWinRate: shortTrades.length > 0 ? (shortWins / shortTrades.length) * 100 : 0,
    longAvgPnl: longTrades.length > 0 ? longPnl / longTrades.length : 0,
    shortAvgPnl: shortTrades.length > 0 ? shortPnl / shortTrades.length : 0,
  };
}

// ── Day of week performance ────────────────────────────────────────────────
export interface DayOfWeekStat {
  day: string;
  dayShort: string;
  pnl: number;
  count: number;
  avgPnl: number;
}

export interface DayOfWeekStats {
  dayStats: DayOfWeekStat[];
  bestDay: DayOfWeekStat | null;
  worstDay: DayOfWeekStat | null;
}

export function calculateDayOfWeekStats(trades: Trade[]): DayOfWeekStats {
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const dayStats = dayNames.map(() => ({ pnl: 0, count: 0 }));

  trades.forEach((trade) => {
    if (trade.pnl !== null) {
      const dayIndex = new Date(trade.date).getDay();
      dayStats[dayIndex].pnl += trade.pnl || 0;
      dayStats[dayIndex].count += 1;
    }
  });

  const daysWithTrades = dayStats
    .map((stat, index) => ({
      day: dayNames[index],
      dayShort: dayNames[index].substring(0, 3),
      pnl: stat.pnl,
      count: stat.count,
      avgPnl: stat.count > 0 ? stat.pnl / stat.count : 0,
    }))
    .filter((d) => d.count > 0);

  const bestDay = daysWithTrades.length > 0
    ? daysWithTrades.reduce((best, current) => (current.avgPnl > best.avgPnl ? current : best))
    : null;
  const worstDay = daysWithTrades.length > 0
    ? daysWithTrades.reduce((worst, current) => (current.avgPnl < worst.avgPnl ? current : worst))
    : null;

  return { dayStats: daysWithTrades, bestDay, worstDay };
}

// ── Streak analysis ────────────────────────────────────────────────────────
export interface Streak {
  type: "win" | "loss";
  length: number;
  totalPnl: number;
}

export interface StreakData {
  longestLoss: Streak;
  longestWin: Streak;
  currentStreak: Streak;
  drawdownScore: number | null;
  drawupScore: number | null;
  drawdownMgmtTotal: number;
  drawdownMgmtOk: number;
  drawupMgmtTotal: number;
  drawupMgmtOk: number;
}

export function calculateStreakData(trades: Trade[]): StreakData | null {
  const closed = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (closed.length === 0) return null;

  const streaks: Array<{ type: "win" | "loss"; length: number; totalPnl: number; trades: Trade[] }> = [];
  let currentType: "win" | "loss" = (closed[0].pnl || 0) >= 0 ? "win" : "loss";
  let currentTrades: Trade[] = [closed[0]];

  for (let i = 1; i < closed.length; i++) {
    const t = closed[i];
    const type: "win" | "loss" = (t.pnl || 0) >= 0 ? "win" : "loss";
    if (type === currentType) {
      currentTrades.push(t);
    } else {
      streaks.push({
        type: currentType,
        length: currentTrades.length,
        totalPnl: currentTrades.reduce((s, x) => s + (x.pnl || 0), 0),
        trades: currentTrades,
      });
      currentType = type;
      currentTrades = [t];
    }
  }
  streaks.push({
    type: currentType,
    length: currentTrades.length,
    totalPnl: currentTrades.reduce((s, x) => s + (x.pnl || 0), 0),
    trades: currentTrades,
  });

  const lossStreaks = streaks.filter((s) => s.type === "loss");
  const winStreaks = streaks.filter((s) => s.type === "win");
  const longestLoss = lossStreaks.reduce((max, s) => (s.length > max.length ? s : max), { type: "loss" as const, length: 0, totalPnl: 0, trades: [] as Trade[] });
  const longestWin = winStreaks.reduce((max, s) => (s.length > max.length ? s : max), { type: "win" as const, length: 0, totalPnl: 0, trades: [] as Trade[] });
  const lastStreak = streaks[streaks.length - 1];

  let drawdownMgmtOk = 0;
  let drawdownMgmtTotal = 0;
  let drawupMgmtOk = 0;
  let drawupMgmtTotal = 0;

  for (let i = 0; i < streaks.length - 1; i++) {
    const streak = streaks[i];
    const nextStreak = streaks[i + 1];
    if (streak.type === "loss" && streak.length >= 2) {
      const avgLossDuringStreak = Math.abs(streak.totalPnl) / streak.length;
      const avgAbsFirstNextTrade = Math.abs(nextStreak.trades[0]?.pnl || 0);
      drawdownMgmtTotal++;
      if (avgAbsFirstNextTrade < avgLossDuringStreak) drawdownMgmtOk++;
    }
    if (streak.type === "win" && streak.length >= 3) {
      const avgWinDuringStreak = streak.totalPnl / streak.length;
      const avgAbsFirstNextTrade = Math.abs(nextStreak.trades[0]?.pnl || 0);
      drawupMgmtTotal++;
      if (avgAbsFirstNextTrade >= avgWinDuringStreak * 0.9) drawupMgmtOk++;
    }
  }

  const drawdownScore = drawdownMgmtTotal > 0 ? Math.round((drawdownMgmtOk / drawdownMgmtTotal) * 100) : null;
  const drawupScore = drawupMgmtTotal > 0 ? Math.round((drawupMgmtOk / drawupMgmtTotal) * 100) : null;

  const strip = (s: { type: "win" | "loss"; length: number; totalPnl: number }): Streak => ({ type: s.type, length: s.length, totalPnl: s.totalPnl });

  return {
    longestLoss: strip(longestLoss),
    longestWin: strip(longestWin),
    currentStreak: strip(lastStreak),
    drawdownScore,
    drawupScore,
    drawdownMgmtTotal,
    drawdownMgmtOk,
    drawupMgmtTotal,
    drawupMgmtOk,
  };
}

// ── Consistency score (0-100) ──────────────────────────────────────────────
export interface ConsistencyScore {
  total: number;
  winRateStability: number;
  riskConsistency: number;
  revengeScore: number;
  drawdownMgmt: number;
}

export function calculateConsistencyScore(trades: Trade[], streakData: StreakData | null): ConsistencyScore | null {
  const closed = trades.filter((t) => t.pnl !== null && t.pnl !== undefined);
  if (closed.length < 5) return null;

  // 1. Win rate stability (25pts)
  const monthlyWinRates: number[] = [];
  const byMonth = new Map<string, Trade[]>();
  for (const t of closed) {
    const key = new Date(t.date).toISOString().substring(0, 7);
    if (!byMonth.has(key)) byMonth.set(key, []);
    byMonth.get(key)!.push(t);
  }
  for (const [, monthTrades] of byMonth) {
    if (monthTrades.length >= 3) {
      const wins = monthTrades.filter((t) => (t.pnl || 0) > 0).length;
      monthlyWinRates.push(wins / monthTrades.length);
    }
  }
  let winRateStabilityScore = 15;
  if (monthlyWinRates.length >= 2) {
    const mean = monthlyWinRates.reduce((s, v) => s + v, 0) / monthlyWinRates.length;
    const variance = monthlyWinRates.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / monthlyWinRates.length;
    const stdDev = Math.sqrt(variance);
    winRateStabilityScore = Math.round(Math.max(0, 25 * (1 - stdDev * 2)));
  }

  // 2. Risk consistency (25pts)
  const absPnls = closed.map((t) => Math.abs(t.pnl || 0));
  const avgAbsPnl = absPnls.reduce((s, v) => s + v, 0) / absPnls.length;
  const pnlVariance = absPnls.reduce((s, v) => s + Math.pow(v - avgAbsPnl, 2), 0) / absPnls.length;
  const pnlStdDev = Math.sqrt(pnlVariance);
  const cvPnl = avgAbsPnl > 0 ? pnlStdDev / avgAbsPnl : 1;
  const riskConsistencyScore = Math.round(Math.max(0, Math.min(25, 25 * (1 - Math.min(cvPnl, 1)))));

  // 3. No-revenge-trading (25pts)
  let revengeCount = 0;
  const sortedByDate = [...closed].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  for (let i = 1; i < sortedByDate.length; i++) {
    const prev = sortedByDate[i - 1];
    const curr = sortedByDate[i];
    const diffMin = (new Date(curr.date).getTime() - new Date(prev.date).getTime()) / 60000;
    if ((prev.pnl || 0) < 0 && diffMin < 30) revengeCount++;
  }
  const revengeRate = revengeCount / Math.max(closed.length - 1, 1);
  const revengeScore = Math.round(Math.max(0, 25 * (1 - revengeRate * 2)));

  // 4. Drawdown management (25pts) — from streak analysis
  let drawdownMgmtScore: number;
  if (!streakData || streakData.drawdownMgmtTotal === 0) {
    drawdownMgmtScore = 25;
  } else if (streakData.drawdownMgmtTotal >= 2) {
    drawdownMgmtScore = Math.round((streakData.drawdownScore ?? 50) * 0.25);
  } else {
    drawdownMgmtScore = (streakData.drawdownScore ?? 50) > 50 ? 20 : 10;
  }

  const total = Math.min(100, winRateStabilityScore + riskConsistencyScore + revengeScore + drawdownMgmtScore);

  return {
    total,
    winRateStability: winRateStabilityScore,
    riskConsistency: riskConsistencyScore,
    revengeScore,
    drawdownMgmt: drawdownMgmtScore,
  };
}

// ── Net daily P&L (aggregate closed trades by calendar date) ────────────────
export interface NetDailyPnlPoint {
  date: string;
  pnl: number;
}

export function calculateNetDailyPnl(trades: Trade[]): NetDailyPnlPoint[] {
  const byDate: Record<string, number> = {};
  trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .forEach((t) => {
      const date = new Date(t.date).toISOString().slice(0, 10);
      byDate[date] = (byDate[date] ?? 0) + netPnl(t);
    });
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, pnl]) => ({ date, pnl: Math.round(pnl * 100) / 100 }));
}

// ── Drawdown curve (peak - cumulative at each trade) ───────────────────────
export function calculateDrawdownCurve(trades: Trade[]): Array<{ x: number; y: number }> {
  const closed = trades
    .filter((t) => t.pnl !== null && t.pnl !== undefined)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  let cumPnl = 0;
  let peak = 0;
  return closed.map((t) => {
    cumPnl += netPnl(t);
    if (cumPnl > peak) peak = cumPnl;
    return { x: new Date(t.date).getTime(), y: -(peak - cumPnl) };
  });
}
